import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { StoreContext } from "../context/StoreContext";
import { TailSpin } from "react-loader-spinner";

const CheckoutBlocked = () => {
  const { deviceId } = useContext(StoreContext);
  const navigate = useNavigate();

  const [cartItems, setCartItems] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch cart summary (no auth required)
  useEffect(() => {
    const fetchCart = async () => {
      if (!deviceId) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`/api/nexus/cart/fetch/v3/${deviceId}`);
        const items = response.data?.data?.items || [];
        setRestaurantId(response.data?.data?.restaurantId || null);
        setCartItems(items);
      } catch (err) {
        console.error("Error fetching cart items:", err);
        setError("Could not load your cart. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [deviceId]);

  // Fetch menu for name/variant resolution (no auth required)
  useEffect(() => {
    const fetchMenu = async () => {
      if (!restaurantId) return;
      try {
        const requestData = {
          type: "COMPLETE",
          restaurantId,
          filters: [{ type: "AVAILABILITY", value: true }],
        };
        const response = await axios.post("/api/catalogue/restaurant/menu/v2", requestData, {
          headers: { "Content-Type": "application/json" },
        });
        const items = response?.data?.data?.items || [];
        setMenuItems(items);
      } catch (err) {
        console.error("Error fetching menu for checkout blocked page:", err);
      }
    };

    fetchMenu();
  }, [restaurantId]);

  const dishMap = useMemo(() => {
    const map = new Map();
    menuItems.forEach((item) => {
      map.set(item.dishId, item);
    });
    return map;
  }, [menuItems]);

  const resolveVariant = (menuDish, cartVariant) => {
    if (!menuDish || !Array.isArray(menuDish.variations)) return undefined;
    const desiredId = cartVariant?.variantId || cartVariant?.variationId;
    return menuDish.variations.find(
      (mv) =>
        mv.variantId === desiredId ||
        mv.variationId === desiredId
    );
  };

  const totalPaise = useMemo(() => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((sum, item) => {
      const menuDish = dishMap.get(item.dishId);
      if (item.type === "BASIC") {
        const price = menuDish?.price ?? item.price ?? 0;
        return sum + price * (item.count || 0);
      }
      if (item.type === "VARIANT" && Array.isArray(item.variants)) {
        const variantTotal = item.variants.reduce((vSum, v) => {
          const menuVariant = resolveVariant(menuDish, v);
          const price = menuVariant?.price ?? v.price ?? 0;
          return vSum + price * (v.count || 0);
        }, 0);
        return sum + variantTotal;
      }
      return sum;
    }, 0);
  }, [cartItems, dishMap]);

  const totalDisplay = useMemo(() => (totalPaise / 100).toFixed(2), [totalPaise]);

  const renderLineItems = () => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
      return <p className="text-center text-gray-600">Your cart is empty.</p>;
    }

    return cartItems.map((item, idx) => {
      const menuDish = dishMap.get(item.dishId);
      const dishName = menuDish?.name || item.dishName || item.dishId;
      if (item.type === "BASIC") {
        const pricePaise = menuDish?.price ?? item.price ?? 0;
        const lineTotal = (pricePaise * (item.count || 0)) / 100;
        return (
          <div key={`${item.dishId}-${idx}`} className="flex justify-between py-2 border-b border-gray-200">
            <div>
              <p className="font-semibold text-[#171725]">{dishName}</p>
              <p className="text-sm text-gray-600">Qty: {item.count || 0}</p>
            </div>
            <div className="text-right">
              <p className="text-[#171725] font-semibold">₹{lineTotal.toFixed(2)}</p>
            </div>
          </div>
        );
      }

      if (item.type === "VARIANT" && Array.isArray(item.variants)) {
        return (
          <div key={`${item.dishId}-${idx}`} className="py-2 border-b border-gray-200">
            <p className="font-semibold text-[#171725] mb-1">{dishName}</p>
            {item.variants.map((v, vIdx) => {
              const menuVariant = resolveVariant(menuDish, v);
              const variantName = menuVariant?.name || v.variantName || v.variantId || v.variationId;
              const displayVariantName = variantName || "Variant";
              const pricePaise = menuVariant?.price ?? v.price ?? 0;
              const lineTotal = (pricePaise * (v.count || 0)) / 100;
              return (
                <div key={`${item.dishId}-${v.variantId}-${vIdx}`} className="flex justify-between pl-4 py-1">
                  <div>
                    <p className="text-sm text-[#171725] font-medium">Variant: {displayVariantName}</p>
                    <p className="text-xs text-gray-600">Qty: {v.count || 0}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#171725] font-semibold">₹{lineTotal.toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      return (
        <div key={`${item.dishId}-${idx}`} className="flex justify-between py-2 border-b border-gray-200">
          <p className="font-semibold text-[#171725]">{item.dishId}</p>
          <p className="text-sm text-gray-600">Unknown item</p>
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <TailSpin height="80" width="80" color="#FBC02D" ariaLabel="loading" radius="1" visible={true} />
      </div>
    );
  }

  return (
    <div className="pt-24 px-4 pb-16 flex flex-col items-center bg-[#FBFCFF] min-h-screen">
      <div className="w-full max-w-3xl">
        <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded mb-6 font-semibold">
          Because you are logged-out, we can't checkout for you
        </div>

        {error && (
          <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <h1 className="text-2xl font-semibold text-[#171725] mb-3">Your Cart</h1>
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          {renderLineItems()}
        </div>

        <div className="flex justify-between items-center bg-white rounded-lg shadow p-4 mb-6">
          <span className="text-lg font-semibold text-[#171725]">Total</span>
          <span className="text-xl font-bold text-[#171725]">₹{totalDisplay}</span>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 bg-white border border-gray-300 text-[#171725] px-4 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
          >
            Go Back
          </button>
          <button
            onClick={() => navigate(restaurantId ? `/menu-items/${restaurantId}` : "/")}
            className="flex-1 bg-[#FBC02D] text-white px-4 py-3 rounded-lg font-semibold hover:bg-[#e5ad28] transition"
          >
            Continue Browsing
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutBlocked;

